import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../../components/Logo/Logo';
import { apiService } from '../../services/api';
import { Plan as ApiPlan } from '../../types';
import './Pricing.css';

/** Display shape: API plan + id (for keys/links) and popular flag */
interface PlanDisplay extends ApiPlan {
  id: string;
  popular: boolean;
}

const faqs = [
  {
    question: 'Posso mudar de plano depois?',
    answer: 'Sim! Você pode fazer upgrade ou downgrade a qualquer momento. O valor é ajustado proporcionalmente.'
  },
  {
    question: 'Como funciona o período de teste?',
    answer: 'O plano Free é gratuito para sempre. Nos planos pagos, você pode testar por 14 dias antes de decidir.'
  },
  {
    question: 'Quais formas de pagamento são aceitas?',
    answer: 'Aceitamos cartão de crédito, boleto bancário e PIX. Pagamentos recorrentes mensais ou anuais.'
  },
  {
    question: 'O que acontece se exceder o limite de leads?',
    answer: 'Você recebe uma notificação quando atingir 80% do limite. Ao atingir 100%, pode fazer upgrade ou aguardar o próximo ciclo.'
  },
  {
    question: 'Posso cancelar a qualquer momento?',
    answer: 'Sim, sem multa ou burocracia. Basta cancelar pelo painel e você terá acesso até o fim do período pago.'
  },
  {
    question: 'Os dados são seguros?',
    answer: 'Sim! Usamos criptografia em trânsito e em repouso. Seus dados ficam em servidores AWS com backup diário.'
  }
];

const Navbar: React.FC = () => (
  <nav className="navbar">
    <div className="container navbar-content">
      <Link to="/" className="navbar-brand">
        <Logo size="small" showText={false} />
      </Link>
      <div className="navbar-links">
        <Link to="/#features">Funcionalidades</Link>
        <Link to="/pricing">Preços</Link>
        <a href="#contact">Contato</a>
      </div>
      <div className="navbar-actions">
        <Link to="/login" className="btn btn-ghost">Entrar</Link>
        <Link to="/register" className="btn btn-primary">Criar Conta</Link>
      </div>
    </div>
  </nav>
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function formatLimit(value: number): string | number {
  return value === -1 ? 'Ilimitado' : value;
}

export const Pricing: React.FC = () => {
  const [isYearly, setIsYearly] = useState(false);
  const [plans, setPlans] = useState<PlanDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService
      .getPlans()
      .then((data) => {
        setPlans(
          data.map((p) => ({
            ...p,
            id: p.planId,
            popular: p.planId === 'starter',
          }))
        );
      })
      .catch((err) => setError(err?.message || 'Erro ao carregar planos'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="pricing-page">
        <div className="container" style={{ padding: '4rem', textAlign: 'center' }}>
          Carregando planos...
        </div>
      </div>
    );
  }
  if (error || plans.length === 0) {
    return (
      <div className="pricing-page">
        <div className="container" style={{ padding: '4rem', textAlign: 'center' }}>
          {error || 'Nenhum plano disponível.'}
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <Navbar />
      
      <section className="pricing-hero">
        <div className="container">
          <h1>Planos e Preços</h1>
          <p>Escolha o plano ideal para sua equipe. Preços justos, sem surpresas.</p>
          
          <div className="billing-toggle">
            <span className={!isYearly ? 'active' : ''}>Mensal</span>
            <button 
              className="toggle-switch"
              onClick={() => setIsYearly(!isYearly)}
              aria-label="Alternar entre mensal e anual"
            >
              <span className={`toggle-slider ${isYearly ? 'yearly' : ''}`}></span>
            </button>
            <span className={isYearly ? 'active' : ''}>
              Anual <span className="discount-badge">-20%</span>
            </span>
          </div>
        </div>
      </section>

      <section className="pricing-plans">
        <div className="container">
          <div className="plans-grid">
            {plans.map((plan) => (
              <div key={plan.id} className={`plan-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && <span className="popular-badge">Mais Popular</span>}
                
                <div className="plan-header">
                  <h3>{plan.name}</h3>
                  <p>{plan.description ?? ''}</p>
                </div>
                
                <div className="plan-price">
                  {plan.priceMonthly < 0 ? (
                    <span className="amount sob-consulta">Sob consulta</span>
                  ) : (
                    <>
                      <span className="currency">R$</span>
                      <span className="amount">
                        {isYearly 
                          ? Math.round(plan.priceYearly / 12) 
                          : plan.priceMonthly}
                      </span>
                      <span className="period">/mês</span>
                    </>
                  )}
                </div>
                
                {isYearly && plan.priceMonthly > 0 && plan.priceMonthly !== -1 && (
                  <p className="yearly-total">
                    R$ {plan.priceYearly}/ano (economia de R$ {plan.priceMonthly * 12 - plan.priceYearly})
                  </p>
                )}
                
                <div className="plan-limits">
                  {plan.priceMonthly < 0 ? (
                    <div className="limit-item">
                      <span className="limit-icon">⚙️</span>
                      <span>Limites personalizados</span>
                    </div>
                  ) : (
                    <>
                      <div className="limit-item">
                        <span className="limit-icon">👥</span>
                        <span>Até {plan.maxUsers} usuários</span>
                      </div>
                      <div className="limit-item">
                        <span className="limit-icon">📋</span>
                        <span>{plan.maxLeadsPerMonth} leads/mês</span>
                      </div>
                      <div className="limit-item">
                        <span className="limit-icon">💬</span>
                        <span>{plan.maxWhatsAppAccounts} contas WhatsApp</span>
                      </div>
                    </>
                  )}
                </div>
                
                {plan.priceMonthly < 0 ? (
                  <a 
                    href="mailto:contato@ghiconnect.com.br?subject=Interesse no plano Enterprise"
                    className="btn btn-block btn-outline"
                  >
                    Fale Conosco
                  </a>
                ) : (
                  <Link 
                    to={`/register?plan=${plan.id}&billing=${isYearly ? 'yearly' : 'monthly'}`}
                    className={`btn btn-block ${plan.popular ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {plan.priceMonthly === 0 ? 'Começar Grátis' : 'Escolher Plano'}
                  </Link>
                )}
                
                <ul className="plan-features">
                  {(plan.features ?? []).map((feature, index) => (
                    <li key={index}>
                      <span className="check-icon">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="comparison">
        <div className="container">
          <h2>Comparativo Completo</h2>
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Funcionalidade</th>
                  <th>Free</th>
                  <th className="highlight">Starter</th>
                  <th>Business</th>
                  <th>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Usuários</td>
                  <td>1</td>
                  <td className="highlight">3</td>
                  <td>10</td>
                  <td>Ilimitados</td>
                </tr>
                <tr>
                  <td>Leads por mês</td>
                  <td>50</td>
                  <td className="highlight">500</td>
                  <td>3.000</td>
                  <td>Ilimitados</td>
                </tr>
                <tr>
                  <td>Contas WhatsApp</td>
                  <td>1</td>
                  <td className="highlight">2</td>
                  <td>5</td>
                  <td>Ilimitadas</td>
                </tr>
                <tr>
                  <td>Dashboard Kanban</td>
                  <td>✓</td>
                  <td className="highlight">✓</td>
                  <td>✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Chat WhatsApp</td>
                  <td>✓</td>
                  <td className="highlight">✓</td>
                  <td>✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Anexos (PDF, imagens)</td>
                  <td>✓</td>
                  <td className="highlight">✓</td>
                  <td>✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Relatórios</td>
                  <td>—</td>
                  <td className="highlight">Básicos</td>
                  <td>Avançados</td>
                  <td>Customizados</td>
                </tr>
                <tr>
                  <td>API de Integração</td>
                  <td>—</td>
                  <td className="highlight">—</td>
                  <td>✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Webhooks</td>
                  <td>—</td>
                  <td className="highlight">—</td>
                  <td>—</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Suporte</td>
                  <td>Email</td>
                  <td className="highlight">Prioritário</td>
                  <td>Chat</td>
                  <td>Dedicado</td>
                </tr>
                <tr>
                  <td>SLA</td>
                  <td>—</td>
                  <td className="highlight">—</td>
                  <td>99%</td>
                  <td>99.9%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="faq">
        <div className="container">
          <h2>Perguntas Frequentes</h2>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h4>{faq.question}</h4>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-cta">
        <div className="container">
          <h2>Ainda tem dúvidas?</h2>
          <p>Nossa equipe está pronta para ajudar você a escolher o melhor plano.</p>
          <div className="cta-buttons">
            <a href="mailto:contato@ghiconnect.com" className="btn btn-white btn-lg">
              Falar com Vendas
            </a>
            <Link to="/register" className="btn btn-outline-white btn-lg">
              Começar Grátis
            </Link>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2026 Ghi Connect. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};
